import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { formatDateToISO } from '@/lib/date-utils';

interface LogDateContextValue {
  /** Selected log date as YYYY-MM-DD */
  dateISO: string;
  date: Date;
  isToday: boolean;
  setDateISO: (iso: string) => void;
  resetToToday: () => void;
  /** ISO timestamp on the selected date, keeping the current time of day */
  timestampFor: () => string;
}

const LogDateContext = createContext<LogDateContextValue | undefined>(undefined);

export const parseISODate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const LogDateProvider = ({ children }: { children: ReactNode }) => {
  const [dateISO, setDateISO] = useState(() => formatDateToISO(new Date()));

  const todayISO = formatDateToISO(new Date());
  const date = useMemo(() => parseISODate(dateISO), [dateISO]);

  const timestampFor = useCallback(() => {
    const now = new Date();
    if (dateISO === formatDateToISO(now)) return now.toISOString();
    const d = parseISODate(dateISO);
    d.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), 0);
    return d.toISOString();
  }, [dateISO]);

  const value = useMemo<LogDateContextValue>(
    () => ({
      dateISO,
      date,
      isToday: dateISO === todayISO,
      setDateISO,
      resetToToday: () => setDateISO(formatDateToISO(new Date())),
      timestampFor,
    }),
    [dateISO, date, todayISO, timestampFor]
  );

  return <LogDateContext.Provider value={value}>{children}</LogDateContext.Provider>;
};

export const useLogDate = (): LogDateContextValue => {
  const ctx = useContext(LogDateContext);
  if (ctx) return ctx;
  // Safe fallback for components rendered outside the provider
  const todayISO = formatDateToISO(new Date());
  return {
    dateISO: todayISO,
    date: new Date(),
    isToday: true,
    setDateISO: () => undefined,
    resetToToday: () => undefined,
    timestampFor: () => new Date().toISOString(),
  };
};
